import useSchedulingPage from "../../../hooks/ReactQueryHooks/useSchedulingPage";
import { listServiceCategories } from "../../api/Scheduling/serviceCategories";
import SchedulingPageControls from "./SchedulingPageControls";
import { Alert } from "@mui/material";
import React, { useEffect, useState } from "react";
import Table from "react-bootstrap/Table";
import { Button } from "../../common/Button";
import { useLocation, useNavigate } from "react-router-dom";
import { schedulingDataChanged } from "../../../lib/schedulingCache";
import { Skeleton, TableCell, Tooltip } from "@mui/material";
import Select from "react-select";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import { failed, success } from "../../common/Toastify";
import { Spinner } from "react-bootstrap";
import EditIcon from "../../../assets/images/edit.png";
import DeleteIcon from "../../../assets/images/delete.png";
import { Link } from "react-router-dom";
import { useSchedulingOrg } from "../../../hooks/ReactQueryHooks/useSchedulingOrg";
import { deactivateServiceCategory } from "../../api/Scheduling/serviceCategories";
import { schedulingErrorMessage } from "../../api/Scheduling/errors";
import { ACTIVE_FILTER_OPTIONS } from "./constants";
import OrganizationSelect from "./OrganizationSelect";
import EditCategory from "./EditCategory";
import "./Scheduling.css";

const CategoryList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getConfirmation } = useConfirmDialog();
  const context = useSchedulingOrg();
  const { organizationRef, excludedOrganizationRefs } = context;
  const [activeFilter, setActiveFilter] = useState("true");
  const [search, setSearch] = useState("");
  const [modalShow, setModalShow] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(()=>{const timer=setTimeout(()=>setDebouncedSearch(search.trim()),300);return()=>clearTimeout(timer);},[search]);


  const activeParam = activeFilter === "all" ? undefined : activeFilter === "true";
  const query = useSchedulingPage("scheduling-service-categories", { organizationRef, scope: "own", excludedOrganizationRefs, active: activeParam, search: debouncedSearch }, listServiceCategories, "serviceCategories");
  const { data: filtered, isLoading, isFetching, refetch } = query;
  useEffect(() => {
    // The create page can be opened directly, before this list exists in cache.
    if (!location.state?.schedulingCreated) return;
    schedulingDataChanged();
    const state = { ...location.state };
    delete state.schedulingCreated;
    navigate(`${location.pathname}${location.search}`, { replace: true, state });
  }, [location, navigate]);

  const handleDelete = async (row) => {
    const confirmed = await getConfirmation({
      title: "Attention!",
      entryName: row.name,
      actionBtnTitle: "Deactivate",
    });
    if (!confirmed) return;
    setDeleting(row.serviceCategoryId);
    deactivateServiceCategory({
      organizationRef: row.organizationRef,
      serviceCategoryId: row.serviceCategoryId,
      expectedVersion: row.version,
    })
      .then(() => {
        success("Schedule category deactivated");
      })
      .catch((error) => failed(schedulingErrorMessage(error)))
      .finally(() => setDeleting(false));
  };

  return (
    <section className="common-listing">
      {modalShow ? (
        <EditCategory
          modalShow={modalShow}
          handleShow={() => setModalShow(false)}
        />
      ) : null}
      <div className="heading-wrap h-change scheduling-category-toolbar">
        <div className="scheduling-category-filters">
          <OrganizationSelect context={context} inputId="category-organization" />
          <div className="scheduling-category-search">
            <input
              id="category-search"
              className="form-control"
              placeholder="Search name or code..."
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
          </div>
          <Select
            inputId="category-active"
            aria-label="Status"
            options={ACTIVE_FILTER_OPTIONS.map((opt) => ({ label: opt.name, value: opt.value }))}
            value={{
              label: ACTIVE_FILTER_OPTIONS.find((opt) => opt.value === activeFilter)?.name,
              value: activeFilter,
            }}
            onChange={(opt) => {
              setActiveFilter(opt?.value || "all");
            }}
            className="scheduling-category-active"
            classNamePrefix="scheduling-category-active"
          />
        </div>
        <div className="right-wrap ml-auto" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Button onClick={() => navigate("/app/add-schedule")} className="custom-btn" style={{ width: "auto" }}>
            Create Schedule Category
          </Button>
        </div>
      </div>
      {query.isError && <Alert severity="error" action={<button onClick={refetch}>Retry</button>}>Could not load appointment types.</Alert>}
      <div className="table-wrap">
        <Table responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Organization</th>
              <th>Code</th>
              <th>Duration</th>
              <th>Increment</th>
              <th>Status</th>
              <th style={{ textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading || isFetching ? (
              [1, 2, 3, 4, 5].map((val) => (
                <tr key={val}>
                  {[1, 2, 3, 4, 5, 6, 7].map((cell) => (
                    <td key={cell}>
                      <Skeleton animation="wave" />
                    </td>
                  ))}
                </tr>
              ))
            ) : filtered.length > 0 ? (
              filtered.map((row) => (
                <tr key={row.serviceCategoryId}>
                  <td>{row.name}</td>
                  <td>{row.organizationName || row.organizationRef}</td>
                  <td>{row.code}</td>
                  <td>{row.durationMinutes} min</td>
                  <td>{row.bookingIncrementMinutes} min</td>
                  <td>
                    <span className={`scheduling-status ${row.active ? "active" : "inactive"}`}>
                      {row.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="actions-styles action-wrap">
                      <Tooltip title="Edit">
                        <Link to="" onClick={() => setModalShow(row)} className="success-btn">
                          <img src={EditIcon} alt="Edit" />
                        </Link>
                      </Tooltip>
                      {row.active ? (
                        <Tooltip title="Deactivate">
                          <Link
                            to=""
                            onClick={() => (deleting ? null : handleDelete(row))}
                            className="delete-btn"
                          >
                            {deleting === row.serviceCategoryId ? (
                              <Spinner size="sm" className="text-current" style={{ color: "white" }} />
                            ) : (
                              <img src={DeleteIcon} alt="Deactivate" />
                            )}
                          </Link>
                        </Tooltip>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <TableCell colSpan={7} style={{ textAlign: "center" }}>
                  No schedule categories
                </TableCell>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <SchedulingPageControls query={query} label="appointment types" />
    </section>
  );
};

export default CategoryList;
